"use client"

import { useContext, useEffect, useMemo, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import AutorenewIcon from "@mui/icons-material/Autorenew"
import FmdGoodIcon from "@mui/icons-material/FmdGood"
import { ThemeProvider } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import CustomPagination from "../transaccionComponent/CustomPagination"
import ModalRrhhFichaje from "./ModalRrhhFichaje.js"
import {
  RRHH_TIPOS_FICHAJE,
  extractErrorMessage,
  formatFechaHora,
  getEmpleadoNombreCompleto,
  getTipoFichajeMeta,
  rrhhTheme,
} from "./rrhhUi.js"

const PAGE_SIZE = 10

const toDateInputValue = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10)

const TablaRrhhFichajesComponent = () => {
  const {
    ubicaciones,
    getUbicacion,
    getRrhhEmpleados,
    getRrhhFichajes,
    postRrhhFichaje,
    postRrhhRecalcularAsistencia,
  } = useContext(ErpContext)

  const [rows, setRows] = useState([])
  const [empleadosOptions, setEmpleadosOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const [searchText, setSearchText] = useState("")
  const [tipoFilter, setTipoFilter] = useState(null)
  const [ubicacionFilter, setUbicacionFilter] = useState(null)
  const [fechaDesde, setFechaDesde] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 6)
    return toDateInputValue(date)
  })
  const [fechaHasta, setFechaHasta] = useState(() => toDateInputValue(new Date()))

  const [modalOpen, setModalOpen] = useState(false)

  const loadCatalogos = async () => {
    await Promise.all([
      ubicaciones.length === 0 ? getUbicacion() : Promise.resolve(ubicaciones),
      getRrhhEmpleados({ page: 1, limit: 200, estadoLaboral: "ACTIVO" }),
    ]).then(([, empleados]) => {
      setEmpleadosOptions(empleados?.data || [])
    })
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await getRrhhFichajes({
        page,
        limit: PAGE_SIZE,
        search: searchText.trim() || undefined,
        fechaDesde,
        fechaHasta,
        tipoEvento: tipoFilter?.value || undefined,
        idUbicacion: ubicacionFilter?.id || undefined,
      })

      setRows(response?.data || [])
      setTotalPages(response?.totalPages || 1)
      setTotalCount(response?.total || 0)
      setError(null)
    } catch (loadError) {
      setError(extractErrorMessage(loadError, "No se pudieron cargar los fichajes RRHH."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCatalogos()
  }, [])

  useEffect(() => {
    loadData()
  }, [page, searchText, tipoFilter, ubicacionFilter, fechaDesde, fechaHasta])

  const handleRefresh = async () => {
    setError(null)
    setSuccess(null)
    await Promise.all([loadCatalogos(), loadData()])
  }

  const handleSaveFichaje = async (payload) => {
    setSaving(true)
    try {
      await postRrhhFichaje({ fichaje: payload })
      setSuccess("Fichaje registrado correctamente.")
      setModalOpen(false)
      await loadData()
    } catch (saveError) {
      setError(extractErrorMessage(saveError, "No se pudo registrar el fichaje."))
    } finally {
      setSaving(false)
    }
  }

  const handleRecalculateByRow = async (row) => {
    try {
      const response = await postRrhhRecalcularAsistencia({
        idEntidad: row.idEntidad,
        fecha: row.fechaHoraServidor,
      })
      setSuccess(
        `Asistencia recalculada. Creadas: ${response?.resumen?.creadas || 0}, actualizadas: ${response?.resumen?.actualizadas || 0}.`
      )
      await loadData()
    } catch (recalcError) {
      setError(extractErrorMessage(recalcError, "No se pudo recalcular la asistencia del día."))
    }
  }

  const fichajesConGeo = useMemo(
    () => rows.filter((row) => row.latitud !== null && row.longitud !== null).length,
    [rows]
  )

  const columns = [
    {
      field: "fechaHoraServidor",
      headerName: "Fecha / hora",
      minWidth: 170,
      flex: 1,
      valueFormatter: (value) => formatFechaHora(value),
    },
    {
      field: "tipoEvento",
      headerName: "Evento",
      minWidth: 150,
      flex: 0.9,
      renderCell: ({ value }) => {
        const meta = getTipoFichajeMeta(value)
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.backgroundColor,
              color: meta.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.4,
      valueGetter: (_, row) => getEmpleadoNombreCompleto(row),
    },
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 120,
      flex: 0.8,
      valueGetter: (_, row) => row.entidad?.rrhhEmpleado?.legajo || "-",
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      minWidth: 160,
      flex: 1,
      valueGetter: (_, row) => row.ubicacion?.descripcion || "-",
    },
    {
      field: "turno",
      headerName: "Turno",
      minWidth: 160,
      flex: 1,
      valueGetter: (_, row) => row.turno?.descripcion || "-",
    },
    {
      field: "fuente",
      headerName: "Fuente",
      minWidth: 110,
      flex: 0.7,
      valueGetter: (_, row) => row.fuente || "-",
    },
    {
      field: "geolocalizacion",
      headerName: "Geo",
      minWidth: 110,
      flex: 0.7,
      renderCell: ({ row }) => {
        if (row.latitud === null || row.longitud === null) {
          return <Typography variant="body2">-</Typography>
        }

        return (
          <Tooltip title={`${row.latitud}, ${row.longitud}${row.precisionGps ? ` · ±${row.precisionGps}m` : ""}`}>
            <Chip
              icon={<FmdGoodIcon fontSize="small" />}
              label="Capturada"
              size="small"
              sx={{
                backgroundColor: alpha("#3b82f6", 0.12),
                color: "#1d4ed8",
                fontWeight: 700,
              }}
            />
          </Tooltip>
        )
      },
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      filterable: false,
      minWidth: 110,
      renderCell: ({ row }) => (
        <Tooltip title="Recalcular asistencia del día">
          <IconButton size="small" onClick={() => handleRecalculateByRow(row)}>
            <AutorenewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Stack spacing={2.5}>
        <Paper sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
          >
            <Box>
              <Typography variant="h5" sx={{ color: "#1f2937", fontWeight: 700 }}>
                Fichajes
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                Registro de eventos crudos de ingreso, egreso y descansos, con soporte para geolocalización web.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                Actualizar
              </Button>
              <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => setModalOpen(true)}>
                Registrar fichaje
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {(error || success) && (
          <Stack spacing={1}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}
          </Stack>
        )}

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
            <TextField
              label="Buscar empleado o legajo"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value)
                setPage(1)
              }}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Autocomplete
              options={RRHH_TIPOS_FICHAJE}
              value={tipoFilter}
              onChange={(_, value) => {
                setTipoFilter(value)
                setPage(1)
              }}
              sx={{ minWidth: 220 }}
              renderInput={(params) => <TextField {...params} label="Tipo de evento" />}
            />

            <Autocomplete
              options={ubicaciones}
              value={ubicacionFilter}
              onChange={(_, value) => {
                setUbicacionFilter(value)
                setPage(1)
              }}
              getOptionLabel={(option) => option.descripcion || String(option.id)}
              sx={{ minWidth: 220 }}
              renderInput={(params) => <TextField {...params} label="Ubicación" />}
            />

            <TextField
              label="Fecha desde"
              type="date"
              value={fechaDesde}
              onChange={(event) => {
                setFechaDesde(event.target.value)
                setPage(1)
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Fecha hasta"
              type="date"
              value={fechaHasta}
              onChange={(event) => {
                setFechaHasta(event.target.value)
                setPage(1)
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Chip
              label={`Con geolocalización: ${fichajesConGeo}`}
              sx={{
                backgroundColor: alpha("#3b82f6", 0.1),
                color: "#1d4ed8",
                fontWeight: 700,
              }}
            />
            <Chip
              label={`Página actual: ${rows.length}`}
              sx={{
                backgroundColor: alpha("#5b5b5f", 0.08),
                color: "#374151",
                fontWeight: 700,
              }}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 1.5 }}>
          <Box sx={{ width: "100%" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              loading={loading}
              hideFooter
              pageSizeOptions={[PAGE_SIZE]}
            />
          </Box>

          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            vieneDe="tablaRrhhFichajes"
            onPageChange={(_, nextPage) => setPage(nextPage)}
          />
        </Paper>
      </Stack>

      <ModalRrhhFichaje
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        onSubmit={handleSaveFichaje}
        empleadosOptions={empleadosOptions}
        ubicacionesOptions={ubicaciones}
        submitting={saving}
      />
    </ThemeProvider>
  )
}

export default TablaRrhhFichajesComponent
